import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { settingsPath, settingsRouteFromPathname } from "@/lib/settingsRoutes";
export type ActiveApp = "actions" | "codesmith" | "proof";

/**
 * Which top-level app the current route belongs to. Codesmith owns the
 * sessions/assistant/automations/MCP connections routes (org-prefixed and
 * legacy bare variants), Proof owns `/proof`, and everything else (runs,
 * monitors, observability, storage, usage, settings, admin) is the GitHub
 * Actions app.
 */
export function useActiveApp() {
  const router = useRouter();
  if (isCodesmithPath(router.pathname)) {
    return "codesmith";
  }
  if (isProofPath(router.pathname)) {
    return "proof";
  }
  return "actions";
}

/** `/codesmith/proof` is also org `codesmith`'s Proof page (see its route). */
export function isProofPath(pathname: string): boolean {
  return /^(\/\[org\]|\/codesmith)?\/proof$/.test(pathname) || pathname === "/[org]/proof/shadow-warm-up";
}
export function isCodesmithPath(pathname: string): boolean {
  return !isProofPath(pathname) && /^(\/\[org\])?\/(sessions|assistant|automations|projects|mcp-connections|codesmith)(\/|$)/.test(pathname);
}

/**
 * Route patterns the desktop app keeps in its window: [code]smith, Proof,
 * canvases, autofix settings, [code]smith and Proof settings, and the `/`
 * and `/[org]` stubs that redirect into sessions there. Everything else opens
 * in the browser.
 */
export function isDesktopPath(pathname: string): boolean {
  return pathname === "/" || pathname === "/[org]" || pathname === "/[org]/canvas/[uuid]" || /^(\/\[org\])?\/(autofix-settings|codesmith-autofix-configuration|enable-autofix)$/.test(pathname) || isCodesmithPath(pathname) || isProofPath(pathname) || isDesktopSettingsPath(pathname);
}
export function isDesktopSettingsPath(pathname: string): boolean {
  const section = settingsRouteFromPathname(pathname)?.section;
  return section === "codesmith" || section === "proof";
}
export function settingsPagePath(org: string | null | undefined, isCodesmithPage: boolean): string {
  return settingsPath(org, isCodesmithPage ? "codesmith" : "general");
}

/**
 * Account-level pages that render full-bleed with only the app-switcher rail —
 * no contextual (second-tier) sidebar. Usage/billing, settings, support, and
 * the admin surface don't belong to either app's navigation, so the GitHub
 * Actions sidebar is omitted there. Settings and admin get their own
 * contextual sidebars in the two-sidebar layout (see SettingsSidebar and
 * AdminSidebar).
 */
export function isHomePath(pathname: string): boolean {
  return pathname === "/[org]/home" || pathname === "/home" ||
  // `/` and `/[org]` only redirect to home, so they share its chrome:
  // mounting the Actions sidebar there flashes it in and out on the way.
  pathname === "/" || pathname === "/[org]";
}
export function isStandaloneAccountPath(pathname: string): boolean {
  return /^(\/\[org\])?\/(usage|support)$/.test(pathname) || isHomePath(pathname) || isSettingsPath(pathname) || isAdminPath(pathname);
}

/** The settings routes (`/settings/**`, `/[org]/settings/**`). */
export function isSettingsPath(pathname: string): boolean {
  return settingsRouteFromPathname(pathname) !== null;
}

/** Superuser admin routes (`/admin`, `/admin/...`). Never org-prefixed. */
export function isAdminPath(pathname: string): boolean {
  return /^\/admin(\/|$)/.test(pathname);
}